import { useState } from "react";
import { exportDataByEmail, insertGdprExportRequest } from "../supabase";
import { useAdminAuth } from "./AdminAuthContext";
import { Search, Download, FileJson } from "lucide-react";

export function AdminGdpr() {
  const { hasPermission } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<{ workshop_subscriptions: unknown[]; orders: unknown[]; email: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasPermission("orders") && !hasPermission("workshops")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don&apos;t have access. GDPR lookup requires orders or workshops permission.</p>;
  }

  const handleLookup = async () => {
    const q = email.trim().toLowerCase();
    if (!q) return;
    setError(null);
    setResult(null);
    setLoading(true);
    const { data, err } = await exportDataByEmail(q);
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setResult(data ?? null);
    await insertGdprExportRequest({
      requested_email: q,
      request_type: "export",
      status: "completed",
      notes: "Admin export (view only)",
    });
  };

  const exportJson = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gdpr-export-${result.email.replace(/[^a-z0-9]/gi, "_")}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCsv = () => {
    if (!result) return;
    const rows: string[] = [];
    const subs = (result.workshop_subscriptions ?? []) as Record<string, unknown>[];
    const ords = (result.orders ?? []) as Record<string, unknown>[];
    if (subs.length > 0) {
      const headers = Object.keys(subs[0]);
      rows.push("workshop_subscriptions", headers.join(","), ...subs.map((s) => headers.map((h) => `"${String(s[h] ?? "").replace(/"/g, '""')}"`).join(",")), "");
    }
    if (ords.length > 0) {
      const headers = Object.keys(ords[0]);
      rows.push("orders", headers.join(","), ...ords.map((o) => headers.map((h) => `"${String(o[h] ?? "").replace(/"/g, '""')}"`).join(",")));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gdpr-export-${result.email.replace(/[^a-z0-9]/gi, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <h1 className="text-[#2D2A26] tracking-[0.08em] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}>
        GDPR — Data lookup & export
      </h1>
      <p className="text-[#6F675F] mb-6 max-w-xl" style={{ fontSize: "13px" }}>
        Admin-only. Look up all data for an email (orders + workshop subscriptions). Export is logged for audit. Do not expose this to the public.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="email"
          placeholder="Email to look up"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLookup()}
          className="px-4 py-2.5 border border-[#E6E0D7] rounded-lg w-80"
          style={{ fontSize: "13px" }}
        />
        <button
          type="button"
          onClick={handleLookup}
          disabled={loading || !email.trim()}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] disabled:opacity-50"
          style={{ fontSize: "12px" }}
        >
          <Search size={16} /> Look up
        </button>
      </div>

      {error && <p className="text-[#8B5C58] mb-4" style={{ fontSize: "12px" }}>{error}</p>}

      {result && (
        <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] p-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-[#2D2A26]" style={{ fontSize: "14px" }}>Data for: {result.email}</span>
            <button type="button" onClick={exportJson} className="flex items-center gap-2 px-3 py-2 border border-[#E6E0D7] rounded-lg text-[#6F675F] hover:bg-[#F2EEE8]" style={{ fontSize: "12px" }}>
              <FileJson size={14} /> Export JSON
            </button>
            <button type="button" onClick={exportCsv} className="flex items-center gap-2 px-3 py-2 border border-[#E6E0D7] rounded-lg text-[#6F675F] hover:bg-[#F2EEE8]" style={{ fontSize: "12px" }}>
              <Download size={14} /> Export CSV
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-[#6F675F] uppercase tracking-wider mb-2" style={{ fontSize: "11px" }}>Workshop subscriptions</h3>
              <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                {(result.workshop_subscriptions ?? []).length} record(s)
              </p>
              <pre className="mt-2 p-3 bg-[#F7F5F1] rounded-lg overflow-auto max-h-48 text-xs" style={{ color: "#2D2A26" }}>
                {JSON.stringify(result.workshop_subscriptions ?? [], null, 2)}
              </pre>
            </div>
            <div>
              <h3 className="text-[#6F675F] uppercase tracking-wider mb-2" style={{ fontSize: "11px" }}>Orders</h3>
              <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                {(result.orders ?? []).length} record(s)
              </p>
              <pre className="mt-2 p-3 bg-[#F7F5F1] rounded-lg overflow-auto max-h-48 text-xs" style={{ color: "#2D2A26" }}>
                {JSON.stringify(result.orders ?? [], null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
